import {view, Icon, Divider, tokens} from '@sail/ui';
import {product as productIcon} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';
import {AgentCallResponse} from '@stripe-internal/conversations-client';
import {useState} from 'react';
import {useWidget} from '../../../Widget/useWidget';

type ProductCardProps = {
  agentCallResponse: AgentCallResponse;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.product_card.title',
    description: 'Product Card title',
    defaultMessage: 'Product',
  },
  noPrice: {
    id: 'conversations_ui.product_card.no_price',
    description: 'Product Card no price text',
    defaultMessage: 'No price',
  },
});

export const ProductCard = ({agentCallResponse}: ProductCardProps) => {
  const intl = useIntl();
  const widget = useWidget();
  const [isHovered, setIsHovered] = useState(false);

  const {product, price: priceObject} = agentCallResponse;

  if (!product) {
    return null;
  }

  const {id: productId, name: productName, images} = product || {};
  const image = images && images.length > 0 ? images[0] : null;
  const {unit_amount: price, currency} = priceObject || {};

  return (
    <view.div
      css={{
        backgroundColor: 'surface',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 1,
        borderStyle: 'solid',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/products/${productId}`,
          },
        });
      }}
    >
      {image ? (
        <view.img
          src={image}
          alt={intl.formatMessage(messages.cardTitle)}
          css={{
            height: '48px',
            width: '48px',
            marginRight: 'space.150',
            borderRadius: 'small',
          }}
        />
      ) : null}
      <view.div
        css={{
          stack: 'y',
          gap: 'xsmall',
        }}
      >
        <view.div
          css={{
            stack: 'x',
            gap: tokens.space[75].toString(),
            alignItems: 'center',
          }}
        >
          <Icon
            icon={productIcon}
            size="xsmall"
            css={{color: isHovered ? 'action.primary' : 'subdued'}}
          />
          <view.span
            css={{
              color: 'subdued',
              font: 'label.small.emphasized',
            }}
          >
            {intl.formatMessage(messages.cardTitle)}
          </view.span>
          {productName ? (
            <view.div css={{height: 10, stack: 'x', width: 0}}>
              <Divider />
            </view.div>
          ) : null}
          {productName ? (
            <view.span
              css={{
                color: 'subdued',
                font: 'label.small.emphasized',
              }}
            >
              {productName}
            </view.span>
          ) : null}
        </view.div>
        {price && currency ? (
          <view.span
            css={{
              font: 'heading.large',
            }}
          >
            {intl.formatNumber(price / 100, {
              style: 'currency',
              currency,
            })}
          </view.span>
        ) : (
          <view.span
            css={{
              font: 'heading.medium',
              color: tokens.color.neutral[300].toString(),
            }}
          >
            {intl.formatMessage(messages.noPrice)}
          </view.span>
        )}
      </view.div>
    </view.div>
  );
};
